import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Typography } from "antd";
import type {
  DispatchAggregateItem,
  DispatchCollectItem,
  DispatchDetectionReport,
  DispatchSenderItem,
} from "../../../shared/dispatchTypes";
import {
  MessageContentView,
  RoomChipList,
  collectCoverageIssues,
  coverageStatusMeta,
  formatClockTime,
  formatTimeRange,
  groupSenders,
  isAggregatedReport,
  isRawSenderId,
  rowMessageLabel,
  type AlignedRow,
  type CoverageIssue,
  type GroupedSender,
  type TargetMatchItem,
} from "./dispatchBoardShared";

const { Text } = Typography;

type Side = "in" | "out";
type Tone = "source" | "collect" | "target";

function ChatSender({ sender }: { sender: GroupedSender }) {
  const rawId = isRawSenderId(sender.label);
  return (
    <span
      className={`dispatch-chat-sender${rawId ? " dispatch-chat-sender--id" : ""}`}
      title={sender.title || undefined}
    >
      {sender.label}
      {sender.count > 1 ? (
        <em className="dispatch-chat-sender__count">×{sender.count}</em>
      ) : null}
      {sender.roomCount > 1 ? (
        <em className="dispatch-chat-sender__rooms">{sender.roomCount} 群</em>
      ) : null}
    </span>
  );
}

/**
 * 发送人列表：全部扫平列齐，不折叠、不省略号截断——昵称必须完整可见。
 * 同名账号先聚合成 “名称 ×N”（服务端只按 userId 去重，同名的会重复出现）。
 * 发送人一多就变成一个多行块，所以只做排版压缩（行距/间距收紧、长昵称允许换行不溢出）。
 */
function ChatSenders({ senders }: { senders: DispatchSenderItem[] }) {
  if (senders.length === 0) {
    return <span className="dispatch-chat-sender dispatch-chat-sender--muted">未知发送人</span>;
  }

  return (
    <span className="dispatch-chat-senders">
      {groupSenders(senders).map((sender) => (
        <ChatSender key={sender.id} sender={sender} />
      ))}
    </span>
  );
}
function ChatMessage({
  side,
  tone,
  roleLabel,
  groupLabel,
  senders,
  item,
  linkDistribution,
  stat,
  onPreview,
}: {
  side: Side;
  tone: Tone;
  roleLabel: string;
  groupLabel?: string;
  senders: DispatchSenderItem[];
  item: DispatchAggregateItem | DispatchCollectItem;
  linkDistribution?: string;
  stat: ChatStatSpec;
  onPreview: (preview: { url: string; title: string }) => void;
}) {
  return (
    <div className={`dispatch-chat-row dispatch-chat-row--${side}`}>
      <div className="dispatch-chat-col">
        <div className="dispatch-chat-meta">
          <span className={`dispatch-chat-role dispatch-chat-role--${tone}`}>{roleLabel}</span>
          <ChatSenders senders={senders} />
          {groupLabel ? (
            <span className="dispatch-chat-group" title={groupLabel}>
              {groupLabel}
            </span>
          ) : null}
        </div>
        <div className={`dispatch-chat-bubble dispatch-chat-bubble--${tone}`}>
          {linkDistribution ? <div className="dispatch-board-link-banner">链接分布：{linkDistribution}</div> : null}
          <MessageContentView item={item} className="dispatch-chat-content" variant="bubble" onPreview={onPreview} />
        </div>
        <div className="dispatch-chat-foot">
          <ChatStat spec={stat} />
          <span className="dispatch-chat-time">{formatTimeRange(item.earliestTs, item.latestTs)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * footer 统计。发单群把覆盖率（命中/配置群数 + 状态）和条数合并到同一个胶囊里，
 * 点击可看群命中详情 —— 之前在气泡内单独占一块 dispatch-chat-badge，与这里的条数重复。
 */
type ChatStatSpec =
  | { kind: "text"; text: string }
  | {
      kind: "coverage";
      matchedCount: number;
      totalCount: number;
      messageCount: number;
      onClick: () => void;
    };

function ChatStat({ spec }: { spec: ChatStatSpec }) {
  if (spec.kind === "text") {
    return <span className="dispatch-chat-stat">{spec.text}</span>;
  }

  const { status, label, Icon } = coverageStatusMeta(spec.matchedCount, spec.totalCount);
  return (
    <button
      type="button"
      className={`dispatch-chat-stat dispatch-chat-stat--coverage dispatch-coverage-badge--${status}`}
      onClick={spec.onClick}
      title="点击查看群命中详情"
    >
      <Icon className="dispatch-chat-stat__icon" />
      <span className="dispatch-chat-stat__ratio">
        <span className="dispatch-chat-stat__matched">{spec.matchedCount}</span>
        <em>/{spec.totalCount}</em>
      </span>
      <span className="dispatch-chat-stat__label">{label}</span>
      <span className="dispatch-chat-stat__count">· {spec.messageCount} 条</span>
    </button>
  );
}

function ChatSystemNote({ text, hint }: { text: string; hint?: string }) {
  return (
    <div className="dispatch-chat-note">
      <span className="dispatch-chat-note__text">{text}</span>
      {hint ? <span className="dispatch-chat-note__hint">{hint}</span> : null}
    </div>
  );
}

function ChatSession({
  row,
  hasSource,
  hasCollect,
  targetGroups,
  groupedTargets,
  highlighted,
  registerRef,
  onOpenRooms,
  onPreview,
}: {
  row: AlignedRow;
  hasSource: boolean;
  hasCollect: boolean;
  targetGroups: DispatchDetectionReport["targetGroups"];
  groupedTargets: boolean;
  highlighted: boolean;
  registerRef: (key: string, node: HTMLElement | null) => void;
  onOpenRooms: (target: TargetMatchItem) => void;
  onPreview: (preview: { url: string; title: string }) => void;
}) {
  return (
    <section
      className={`dispatch-chat-session${highlighted ? " dispatch-chat-session--highlight" : ""}`}
      ref={(node) => registerRef(row.key, node)}
    >
      <div className="dispatch-chat-day">
        <span>{formatTimeRange(row.earliestTs, row.latestTs)}</span>
      </div>

      {hasSource ? (
        row.source ? (
          <ChatMessage
            side="in"
            tone="source"
            roleLabel="源头群"
            senders={row.source.senders}
            item={row.source}
            stat={{ kind: "text", text: `${row.source.messageCount} 条 · ${row.source.roomCount} 个群` }}
            onPreview={onPreview}
          />
        ) : (
          <ChatSystemNote text="未命中源头群" />
        )
      ) : null}

      {hasCollect ? (
        row.collect ? (
          <ChatMessage
            side="in"
            tone="collect"
            roleLabel="采集群"
            senders={row.collect.senders}
            item={row.collect}
            stat={{ kind: "text", text: `${row.collect.roomCount} 个群` }}
            onPreview={onPreview}
          />
        ) : (
          <ChatSystemNote text="未命中采集群" />
        )
      ) : null}

      {targetGroups.length > 0 ? (
        targetGroups.map((group) => {
          const target = row.targets.find((item) => item.groupId === group.groupId) || null;
          const groupLabel = groupedTargets ? group.groupName : "";
          if (!target) {
            return (
              <ChatSystemNote
                key={group.groupId}
                text={groupedTargets ? `未发单 · ${group.groupName}` : "未发单"}
                hint="源头/采集已有，发单群未命中"
              />
            );
          }
          return (
            <ChatMessage
              key={group.groupId}
              side="out"
              tone="target"
              roleLabel="发单群"
              groupLabel={groupLabel || undefined}
              senders={target.item.senders}
              item={target.item}
              linkDistribution={target.item.linkDistribution}
              stat={{
                kind: "coverage",
                matchedCount: target.item.targetHitRoomCount || 0,
                totalCount: target.item.targetConfiguredRoomCount || target.roomCount,
                messageCount: target.item.targetMessageCount || 0,
                onClick: () => onOpenRooms(target),
              }}
              onPreview={onPreview}
            />
          );
        })
      ) : (
        <ChatSystemNote text="暂无发单群配置" />
      )}
    </section>
  );
}

/** 左栏里的一条漏发记录：消息本身 + 差在哪个分组。 */
function ChatIssueItem({
  issue,
  active,
  onClick,
}: {
  issue: CoverageIssue;
  active: boolean;
  onClick: () => void;
}) {
  const { Icon, label } = coverageStatusMeta(
    issue.gaps[0]?.matchedCount || 0,
    issue.gaps[0]?.totalCount || 0
  );
  const messageText = rowMessageLabel(issue.row);
  const first = issue.gaps[0];
  const restCount = issue.gaps.length - 1;

  return (
    <button
      type="button"
      className={`dispatch-chat-issue dispatch-chat-issue--${issue.worst}${active ? " dispatch-chat-issue--active" : ""}`}
      onClick={onClick}
      title={first ? `${first.groupName}：${first.matchedCount}/${first.totalCount} ${label}` : label}
    >
      <span className="dispatch-chat-issue__body">
        <span className="dispatch-chat-issue__content">{messageText}</span>
        <span className="dispatch-chat-issue__meta">
          <span className={`dispatch-chat-issue__status dispatch-chat-issue__status--${issue.worst}`}>
            <Icon className="dispatch-chat-issue__icon" />
            {first ? `${first.matchedCount}/${first.totalCount}` : "-"} {label}
          </span>
          {first && issue.coverage.length > 1 ? (
            <span className="dispatch-chat-issue__group">{first.groupName}</span>
          ) : null}
          {restCount > 0 ? <span className="dispatch-chat-issue__more">+{restCount} 个分组</span> : null}
        </span>
      </span>
      <span className="dispatch-chat-issue__time">{formatClockTime(issue.row.latestTs)}</span>
    </button>
  );
}

function ChatIssuePanel({
  issues,
  activeKey,
  onJump,
}: {
  issues: CoverageIssue[];
  activeKey: string | null;
  onJump: (key: string) => void;
}) {
  return (
    <aside className="dispatch-chat-issues">
      <div className="dispatch-chat-issues__head">
        <span className="dispatch-chat-issues__title">未发全的消息</span>
        <span className="dispatch-chat-issues__count">{issues.length}</span>
      </div>
      {issues.length === 0 ? (
        <div className="dispatch-chat-issues__empty">所有消息都已发全</div>
      ) : (
        <div className="dispatch-chat-issues__list">
          {issues.map((issue) => (
            <ChatIssueItem
              key={issue.key}
              issue={issue}
              active={issue.key === activeKey}
              onClick={() => onJump(issue.key)}
            />
          ))}
        </div>
      )}
    </aside>
  );
}

/** 判定“算不算贴到底部”的容差（px）。 */
const STICK_TO_BOTTOM_THRESHOLD = 48;

/**
 * 聊天界面视图：仿微信的会话流。
 * 源头群 / 采集群在左侧（别人发的），发单群在右侧（我们发的）。
 * 行序按会话流习惯：旧的在上，最新的在底部，并在刷新/数据轮询后贴住底部。
 * 左栏是“未发全”清单，点一条会滚到对应的那条会话并高亮。
 */
export function DispatchChatBoard({
  report,
  rows,
  onOpenRooms,
  onPreview,
}: {
  report: DispatchDetectionReport;
  rows: AlignedRow[];
  onOpenRooms: (target: TargetMatchItem) => void;
  onPreview: (preview: { url: string; title: string }) => void;
}) {
  const aggregated = isAggregatedReport(report);
  const targetGroups = useMemo(
    () => [...report.targetGroups].sort((a, b) => a.groupName.localeCompare(b.groupName, "zh-CN")),
    [report.targetGroups]
  );
  const issues = useMemo(() => collectCoverageIssues(rows, targetGroups), [rows, targetGroups]);
  // 会话流：旧的在上、最新的在底部（rows 本身是最新在上，所以翻转）
  const orderedRows = useMemo(() => [...rows].reverse(), [rows]);
  const hasSource = report.sourceRooms.length > 0;
  const hasCollect = report.collectRooms.length > 0;
  // 聚合视图下所有目标群已并成一列，气泡上就不用再标分组名了
  const groupedTargets = targetGroups.length > 1;

  const streamRef = useRef<HTMLDivElement | null>(null);
  const sessionRefs = useRef(new Map<string, HTMLElement>());
  /** 是否跟随最新：用户自己往上翻了就不再抢滚动条。 */
  const pinnedRef = useRef(true);
  const [showJumpLatest, setShowJumpLatest] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [highlightKey, setHighlightKey] = useState<string | null>(null);

  const registerSession = useCallback((key: string, node: HTMLElement | null) => {
    if (node) sessionRefs.current.set(key, node);
    else sessionRefs.current.delete(key);
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = streamRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  // 首次挂载 + 数据刷新：贴住底部
  useLayoutEffect(() => {
    if (!pinnedRef.current) return;
    scrollToBottom();
  }, [orderedRows, scrollToBottom]);

  // 图片 / 媒体是异步撑高的，只靠上面那次同步滚动会“差一截”——这也是刷新后
  // 看起来“没滚到底部”的根源。
  // 两条路都接上：ResizeObserver 管任意高度变化，图片 load 事件管异步加载。
  // 只加 ResizeObserver 不够：它的回调在渲染帧里派发，丢帧就不会触发；
  // load 事件是普通任务，不受帧影响。
  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    const stick = () => {
      if (pinnedRef.current) el.scrollTop = el.scrollHeight;
    };
    const inner = el.firstElementChild;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(stick);
    if (observer && inner) observer.observe(inner);
    // load 不冒泡，所以要监听整个会话流并走捕获阶段
    el.addEventListener("load", stick, true);
    return () => {
      observer?.disconnect();
      el.removeEventListener("load", stick, true);
    };
  }, []);

  const syncPinned = useCallback(() => {
    const el = streamRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_TO_BOTTOM_THRESHOLD;
    pinnedRef.current = atBottom;
    setShowJumpLatest((prev) => (prev === !atBottom ? prev : !atBottom));
  }, []);

  const jumpToRow = useCallback((key: string) => {
    const stream = streamRef.current;
    const target = sessionRefs.current.get(key);
    if (!stream || !target) return;
    // 手动定位后不再自动贴底，否则数据一轮询就把用户拉回去
    pinnedRef.current = false;
    setShowJumpLatest(true);
    setActiveKey(key);
    setHighlightKey(key);
    const delta = target.getBoundingClientRect().top - stream.getBoundingClientRect().top;
    // 即时跳，不用 behavior: smooth：跨几千像素的平滑滚动又慢又不稳定，
    // 而且它靠 rAF 驱动，一旦丢帧就会停在半路（看起来像“点了没反应”）——
    // 这里改用确定性的 scrollTop 赋值，任何环境都落到位。
    stream.scrollTo({ top: Math.max(0, stream.scrollTop + delta - 8), behavior: "auto" });
  }, []);

  useEffect(() => {
    if (!highlightKey) return;
    const timer = window.setTimeout(() => setHighlightKey(null), 1800);
    return () => window.clearTimeout(timer);
  }, [highlightKey]);

  const backToLatest = useCallback(() => {
    pinnedRef.current = true;
    setActiveKey(null);
    scrollToBottom();
    setShowJumpLatest(false);
  }, [scrollToBottom]);

  return (
    <div className="dispatch-chat">
      <ChatIssuePanel issues={issues} activeKey={activeKey} onJump={jumpToRow} />

      <div className="dispatch-chat-main">
        <div className="dispatch-chat-peers">
        <span className="dispatch-chat-peers__title">会话成员</span>
        {hasSource ? (
          <div className="dispatch-chat-peer dispatch-chat-peer--source">
            <span className="dispatch-chat-peer__label">源头群</span>
            <div className="dispatch-chat-peer__body">
              <RoomChipList rooms={report.sourceRooms} />
            </div>
          </div>
        ) : null}
        {hasCollect ? (
          <div className="dispatch-chat-peer dispatch-chat-peer--collect">
            <span className="dispatch-chat-peer__label">采集群</span>
            <div className="dispatch-chat-peer__body">
              <RoomChipList rooms={report.collectRooms} />
            </div>
          </div>
        ) : null}
        <div className="dispatch-chat-peer dispatch-chat-peer--target">
          <span className="dispatch-chat-peer__label">发单群</span>
          <div className="dispatch-chat-peer__body">
            {aggregated ? (
              <span className="dispatch-board-unit-name" title={report.businessUnits.map((unit) => unit.name).join("、")}>
                {report.businessUnits.length} 个业务单元合并
              </span>
            ) : null}
            {targetGroups.length === 0 ? (
              <Text type="secondary">暂无发单群配置</Text>
            ) : (
              <div className="dispatch-board-room-chips">
                {targetGroups.map((group) => (
                  <span key={group.groupId} className="dispatch-board-room-chip" title={group.groupName}>
                    {group.groupName} · {group.roomCount} 群
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dispatch-chat-stream" ref={streamRef} onScroll={syncPinned}>
        <div className="dispatch-chat-stream__inner">
          {orderedRows.map((row) => (
            <ChatSession
              key={row.key}
              row={row}
              hasSource={hasSource}
              hasCollect={hasCollect}
              targetGroups={targetGroups}
              groupedTargets={groupedTargets}
              highlighted={row.key === highlightKey}
              registerRef={registerSession}
              onOpenRooms={onOpenRooms}
              onPreview={onPreview}
            />
          ))}
        </div>
      </div>

        {showJumpLatest ? (
          <button type="button" className="dispatch-chat-jump" onClick={backToLatest}>
            回到最新
          </button>
        ) : null}
      </div>
    </div>
  );
}
